/**
 * Expense List Page
 *
 * Displays all expenses with filtering and export capabilities
 *
 * @module pages/expenses/ExpenseList
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
  Alert,
  Dropdown,
  Modal,
  Collapse,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import { listExpenses, exportExpensesToCSV, deleteExpense } from '../../services/expenseService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { listProjects } from '../../services/projectService';
import {
  formatCurrency,
  formatDate,
  formatExpenseCategory,
} from '../../utils/formatters';
import { EXPENSE_CATEGORY } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCSV, generateFilename } from '../../utils/exportHelpers';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ExpenseList.css';

/**
 * ExpenseList component
 */
const ExpenseList = () => {
  const navigate = useNavigate();
  const { isAdmin, isTechnician, isSupervisor } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const [filters, setFilters] = useState({
    projectId: '',
    category: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 0,
    pageSize: 20,
  });

  const [isExporting, setIsExporting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState('image'); // 'image' or 'pdf'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Fetch expenses
  const {
    data: expensesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['expenses', filters],
    // Sin `unwrapServiceResult`, el `{data:null, error}` de listExpenses llega
    // como éxito y el bloque `if (error)` de más abajo es inalcanzable: un fallo
    // se muestra como "no hay gastos registrados". Ver utils/serviceResult.js.
    queryFn: () => unwrapServiceResult(listExpenses(filters)),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new
  });

  // Fetch projects for filter
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { pageSize: 100 }],
    queryFn: () => listProjects({ pageSize: 100 }),
  });

  const expenses = expensesData?.data || [];
  const totalCount = expensesData?.count || 0;
  const projects = projectsData?.data || [];
  const totalPages = Math.ceil(totalCount / filters.pageSize);
  const currentPage = filters.page + 1;

  const expenseColumns = useMemo(() => {
    const cols = [
      {
        id: 'id',
        header: 'ID',
        accessor: 'id',
        minWidth: 100,
        cell: (e) => (
          <small className="text-muted font-monospace">{e.id?.substring(0, 8)}...</small>
        ),
      },
      {
        id: 'expense_date',
        header: 'Fecha',
        accessor: 'expense_date',
        minWidth: 110,
        cell: (e) => formatDate(e.expense_date),
      },
      {
        id: 'project',
        header: 'Proyecto',
        accessor: (e) => e.project?.project_number || '',
        minWidth: 130,
        cell: (e) => e.project ? (
          <Button
            variant="link"
            className="p-0 text-start"
            onClick={(ev) => { ev.stopPropagation(); navigate(`/proyectos/${e.project.id}`); }}
          >
            {e.project.project_number}
          </Button>
        ) : '-',
      },
      {
        id: 'task',
        header: 'Tarea',
        accessor: (e) => e.task?.task_number || '',
        minWidth: 110,
        cell: (e) => e.task ? (
          <Button
            variant="link"
            className="p-0 text-start"
            size="sm"
            onClick={(ev) => { ev.stopPropagation(); navigate(`/tareas/${e.task.id}`); }}
          >
            {e.task.task_number}
          </Button>
        ) : <span className="text-muted">-</span>,
      },
      {
        id: 'category',
        header: 'Categoría',
        accessor: 'category',
        minWidth: 130,
        cell: (e) => <Badge bg="secondary">{formatExpenseCategory(e.category)}</Badge>,
      },
      {
        id: 'vendor',
        header: 'Proveedor',
        accessor: (e) => e.vendor || '',
        minWidth: 140,
        cell: (e) => e.vendor || '-',
      },
      {
        id: 'amount',
        header: 'Monto',
        accessor: (e) => e.amount || 0,
        align: 'right',
        minWidth: 110,
        cell: (e) => <strong>{formatCurrency(e.amount)}</strong>,
      },
      {
        id: 'notes',
        header: 'Notas',
        accessor: (e) => e.notes || '',
        minWidth: 140,
        cell: (e) => e.notes ? (
          <span
            title={e.notes}
            style={{ maxWidth: 220, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {e.notes}
          </span>
        ) : <span className="text-muted">-</span>,
      },
      {
        id: 'receipt',
        header: 'Recibo',
        accessor: (e) => (e.receipt_url ? 1 : 0),
        align: 'center',
        minWidth: 80,
        cell: (e) => e.receipt_url ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(ev) => { ev.stopPropagation(); handleImageClick(e.receipt_url); }}
            title="Ver recibo"
          >
            <i className={`bi ${e.receipt_url.match(/\.pdf(\?|$)/i) ? 'bi-file-earmark-pdf' : 'bi-image'}`}></i>
          </Button>
        ) : <span className="text-muted">-</span>,
      },
      {
        id: 'created_by',
        header: 'Creado Por',
        accessor: (e) => e.created_by_profile?.full_name || '',
        minWidth: 140,
        cell: (e) => (
          <small className="text-muted">{e.created_by_profile?.full_name || '-'}</small>
        ),
      },
    ];

    if (isAdmin || isTechnician) {
      cols.push({
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (expense) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones del gasto de ${expense.vendor || 'proveedor sin nombre'}`}>
              <Dropdown.Item onClick={() => navigate(`/gastos/${expense.id}/editar`)}>
                <i className="bi bi-pencil me-2"></i>Editar
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => handleDeleteClick(expense)}>
                <i className="bi bi-trash me-2"></i>Eliminar
              </Dropdown.Item>
            </KebabMenu>
          </div>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isAdmin, isTechnician]);

  // Ref for debounce timer
  const searchTimeoutRef = useRef(null);

  /**
   * Handle filter change
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0, // Reset to first page
    }));
  };

  /**
   * Handle search input change with debounce (300ms delay)
   */
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: value,
        page: 0,
      }));
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Computed: count active filters (excluding search)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.projectId) count++;
    if (filters.category) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  }, [filters.projectId, filters.category, filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = activeFiltersCount > 0 || filters.search;

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      projectId: '',
      category: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      page: 0,
    }));
    setSearchInput('');
  };

  /**
   * Toggle filters panel
   */
  const toggleFiltersPanel = () => setIsFiltersExpanded((prev) => !prev);

  /**
   * Handle export to CSV
   */
  const handleExport = async () => {
    try {
      setIsExporting(true);
      toast.info('Exportando...');

      const csv = exportExpensesToCSV(expenses);
      const filename = generateFilename('gastos');
      downloadCSV(csv, filename);

      toast.success('CSV descargado exitosamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar CSV');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handle pagination
   */
  const handlePreviousPage = () => {
    if (filters.page > 0) {
      handleFilterChange('page', filters.page - 1);
    }
  };

  const handleNextPage = () => {
    if (filters.page < totalPages - 1) {
      handleFilterChange('page', filters.page + 1);
    }
  };

  /**
   * Handle receipt click to show in modal
   *
   * `receiptUrl` ya llega VIVO: `expenseService` guarda el path en la base y
   * re-firma la URL en cada lectura. Acá había un parche que re-extraía el
   * path con un regex (`/\/receipts\/(.+)$/`) y volvía a firmar, porque el
   * valor guardado era una signed URL vencida. Ese parche se retiró junto con
   * la causa; no hay que reintroducirlo.
   */
  const handleImageClick = (receiptUrl) => {
    setShowImageModal(true);
    setSelectedFileType(/\.pdf(\?|$)/i.test(receiptUrl) ? 'pdf' : 'image');
    setSelectedImage(receiptUrl);
  };

  /**
   * Download receipt file
   */
  const handleDownloadReceipt = async () => {
    if (!selectedImage) return;
    try {
      const response = await fetch(selectedImage);
      const blob = await response.blob();
      const ext = selectedFileType === 'pdf' ? 'pdf' : 'png';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recibo_${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al descargar el recibo');
    }
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteModal(true);
  };

  /**
   * Handle expense deletion
   */
  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      setIsDeleting(true);
      const { success, error: deleteError } = await deleteExpense(expenseToDelete.id);

      if (deleteError) {
        toast.error('Error al eliminar el gasto');
        return;
      }

      if (success) {
        toast.success('Gasto eliminado exitosamente');
        refetch();
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error al eliminar el gasto');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setExpenseToDelete(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando gastos...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar gastos</Alert.Heading>
          <p>{error.message}</p>
          <Button variant="outline-danger" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">Gastos</h2>
              <p className="text-muted">{totalCount} gastos totales</p>
            </div>
            <div className="d-flex gap-2">
              {expenses.length > 0 && (
                <Button
                  variant="outline-success"
                  onClick={handleExport}
                  disabled={isExporting}
                  data-testid="export-csv-button"
                >
                  {isExporting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-1"
                        data-testid="export-loading-spinner"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-1"></i>
                      Exportar CSV
                    </>
                  )}
                </Button>
              )}
              {/*
                El supervisor REGISTRA gastos (matriz + `supervisor_create_expenses`
                en la base), pero no los edita ni los borra: el kebab de arriba
                sigue sin incluirlo, y es a proposito.
              */}
              {(isAdmin || isTechnician || isSupervisor) && (
                <Button variant="primary" onClick={() => navigate('/gastos/nuevo')}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Registrar Gasto
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Search & Filters */}
      <div className="expense-search-filter mb-4">
        {/* Global search bar + filter toggle */}
        <div className="expense-global-search">
          <InputGroup className="flex-grow-1">
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar gastos..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar en gastos"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={toggleFiltersPanel}
            aria-expanded={isFiltersExpanded}
            aria-controls="expense-advanced-filters"
            className="filter-toggle-btn"
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="filter-count-badge">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Collapsible advanced filters panel */}
        <Collapse in={isFiltersExpanded}>
          <div id="expense-advanced-filters">
            <div className="advanced-filters-panel">
              <Row className="g-2 g-md-3">
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Proyecto</Form.Label>
                  <Form.Select
                    value={filters.projectId}
                    onChange={(e) => handleFilterChange('projectId', e.target.value)}
                    aria-label="Filtrar por proyecto"
                    size="sm"
                  >
                    <option value="">Todos los proyectos</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.project_number}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Categoría</Form.Label>
                  <Form.Select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    aria-label="Filtrar por categoría"
                    size="sm"
                  >
                    <option value="">Todas las categorías</option>
                    <option value={EXPENSE_CATEGORY.MATERIALS}>Materiales</option>
                    <option value={EXPENSE_CATEGORY.TOOLS}>Herramientas</option>
                    <option value={EXPENSE_CATEGORY.LABOR}>Mano de Obra</option>
                    <option value={EXPENSE_CATEGORY.TRANSPORTATION}>Transporte</option>
                    <option value={EXPENSE_CATEGORY.OTHER}>Otros</option>
                  </Form.Select>
                </Col>

                <Col xs={6} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    aria-label="Fecha desde"
                    size="sm"
                  />
                </Col>

                <Col xs={6} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    aria-label="Fecha hasta"
                    size="sm"
                  />
                </Col>
              </Row>

              {/* Clear filters button */}
              {hasActiveFilters && (
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearFilters}
                    className="clear-filters-btn"
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Limpiar filtros
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      {/* Expense Table */}
      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {expenses.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-receipt" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  <h5 className="mt-3 text-muted">No se encontraron gastos</h5>
                  <p className="text-muted">
                    {filters.projectId || filters.category || filters.search
                      ? 'Intenta cambiar los filtros'
                      : (isAdmin || isTechnician || isSupervisor)
                      ? 'Registra tu primer gasto'
                      : 'No hay gastos disponibles'}
                  </p>
                </div>
              ) : (
                <>
                  {isTableView ? (
                    <DataTable
                      tableId="expenses"
                      data={expenses}
                      rowKey={(e) => e.id}
                      columns={expenseColumns}
                    />
                  ) : (
                    <Row className="g-3 p-3">
                      {expenses.map((expense) => (
                        <Col xs={12} sm={6} lg={4} key={expense.id}>
                          <Card className="expense-mobile-card shadow-sm">
                            <Card.Body style={{ position: 'relative' }}>
                              {/* Kebab - top right corner */}
                              {(isAdmin || isTechnician) && (
                                <KebabMenu
                                  label={`Acciones del gasto de ${expense.vendor || 'proveedor sin nombre'}`}
                                  stopPropagation={false}
                                  dropdownProps={{ style: { position: 'absolute', top: '12px', right: '12px' } }}
                                >
                                  <Dropdown.Item
                                    onClick={() => navigate(`/gastos/${expense.id}/editar`)}
                                  >
                                    <i className="bi bi-pencil me-2"></i>
                                    Editar
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => handleDeleteClick(expense)}
                                  >
                                    <i className="bi bi-trash me-2"></i>
                                    Eliminar
                                  </Dropdown.Item>
                                </KebabMenu>
                              )}
                              {/* Title: Vendor Name */}
                              <h6 className="mb-2 fw-bold pe-4">
                                {expense.vendor || 'Sin proveedor'}
                              </h6>
                              {/* ID - Full */}
                              <div className="mb-2">
                                <small className="text-muted font-monospace" style={{ wordBreak: 'break-all' }}>
                                  ID: {expense.id}
                                </small>
                              </div>
                              {/* Header: Date, Amount */}
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <small className="text-muted d-block">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {formatDate(expense.expense_date)}
                                  </small>
                                  <Badge bg="secondary" className="mt-1">
                                    {formatExpenseCategory(expense.category)}
                                  </Badge>
                                </div>
                                <span className="fs-5 fw-bold text-primary">
                                  {formatCurrency(expense.amount)}
                                </span>
                              </div>

                              {/* Notes */}
                              {expense.notes && (
                                <div className="mb-2">
                                  <small className="text-muted">Notas:</small>
                                  <div className="small">{expense.notes}</div>
                                </div>
                              )}

                              {/* Project and Task */}
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {expense.project && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate(`/proyectos/${expense.project.id}`)}
                                  >
                                    <i className="bi bi-folder me-1"></i>
                                    {expense.project.project_number}
                                  </Button>
                                )}
                                {expense.task && (
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => navigate(`/tareas/${expense.task.id}`)}
                                  >
                                    <i className="bi bi-list-task me-1"></i>
                                    {expense.task.task_number}
                                  </Button>
                                )}
                              </div>

                              {/* Footer: Created by and Receipt */}
                              <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                <small className="text-muted">
                                  <i className="bi bi-person me-1"></i>
                                  {expense.created_by_profile?.full_name || '-'}
                                </small>
                                {expense.receipt_url && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleImageClick(expense.receipt_url)}
                                    title="Ver recibo"
                                  >
                                    <i className={`bi ${expense.receipt_url.match(/\.pdf(\?|$)/i) ? 'bi-file-earmark-pdf' : 'bi-image'} me-1`}></i>
                                    <small>Ver recibo</small>
                                  </Button>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                      <div className="text-muted small">
                        Página {currentPage} de {totalPages}
                      </div>
                      <div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handlePreviousPage}
                          disabled={filters.page === 0}
                          className="me-2"
                        >
                          <i className="bi bi-chevron-left"></i> Anterior
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={filters.page >= totalPages - 1}
                        >
                          Siguiente <i className="bi bi-chevron-right"></i>
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Receipt Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Recibo</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-3">
          {selectedImage ? (
            selectedFileType === 'pdf' ? (
              <iframe
                src={selectedImage}
                title="Recibo PDF"
                style={{
                  width: '100%',
                  height: '70vh',
                  border: 'none',
                }}
              />
            ) : (
              <img
                src={selectedImage}
                alt="Recibo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            )
          ) : (
            <div className="py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando recibo...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-success"
            onClick={handleDownloadReceipt}
            disabled={!selectedImage}
          >
            <i className="bi bi-download me-1"></i>
            Descargar
          </Button>
          <Button
            variant="outline-primary"
            href={selectedImage}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!selectedImage}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            Abrir en nueva pestaña
          </Button>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar este gasto?</p>
          {expenseToDelete && (
            <div className="bg-light p-3 rounded">
              <strong>{formatCurrency(expenseToDelete.amount)}</strong>
              <span className="text-muted ms-2">
                {expenseToDelete.vendor || 'Sin proveedor'}
              </span>
              <br />
              <small className="text-muted">
                {formatDate(expenseToDelete.expense_date)}
              </small>
            </div>
          )}
          <p className="text-danger mt-3 mb-0">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Esta acción no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Eliminando...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default ExpenseList;
